import PromptDeleteAction from "@common-components/prompt-delete-action/prompt-delete-action";
import SelectUserRole from "@common-components/select-user-role";
import PersonAvatar from "@common-components/tasks/person-avatar/person-avatar";
import { userDisplayName } from "@common-models/user";
import { Workspace } from "@common-models/workspace";
import { BlueticksWorkspaceUser, ValidManualStatusList } from "@common-models/workspace-user";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Box,
  Typography
} from "@mui/material";
import { getSortedAndFilteredWorkspaceUserList } from "@common-reducers/BoardsSelectors";
import {
  DBWorkspaceUserThunks
} from "@common-reducers/DBServiceThunks";
import { taskActions } from "@common-reducers/TasksReducer";
import { selectCurrentWorkspace } from "@common-reducers/UserSelectors";
import {
  useAuthenticateMutation,
  useAssignWorkspaceUserInviteIdMutation
} from "@common-reducers/backend-api/backend-api";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { namespace } from "@common-config/constants";
import { appConfig } from "@common-config/app.config";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function WorkspaceUsersMembers({readOnly}: { readOnly?: boolean }) {
  const { t } = useTranslation(namespace);
  const direction = useAppSelector((state) => state.UserReducer.direction);

  const sortedAndFilteredWorkspaceUserList = useAppSelector(state => getSortedAndFilteredWorkspaceUserList(state));


  const currentWorkspace = useAppSelector(selectCurrentWorkspace);


  const cellHeadStyle = {
    fontFamily: '"Inter", "Assistant", sans-serif',
    padding: '12px 16px',
    color: 'var(--color-text)',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '16px',
    borderBottom: '1px solid var(--border-color, #E5E7EB)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  }

  const buildTable = () => (
    <>
      <TableContainer sx={{
        background: 'var(--background-default)',
        overflow: 'hidden',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        marginTop: "24px",
        borderRadius: '12px',
      }} component={Paper}>
        <Table sx={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
        }}>
          <TableHead sx={{
            background: 'var(--color-bg)'
          }}>
            <TableRow>
              <TableCell align={direction === "ltr" ? "left" : "right"} sx={{
                ...cellHeadStyle,
                width: '30%'
              }}>{t("workspaceUsersMembers.headerName")}</TableCell>
              <TableCell align={direction === "ltr" ? "left" : "right"} sx={{
                ...cellHeadStyle,
                width: '25%'
              }} >{t("workspaceUsersMembers.headerEmail")}</TableCell>
              <TableCell align={direction === "ltr" ? "left" : "right"} sx={{
                ...cellHeadStyle,
                width: '15%'
              }} >{t("workspaceUsersMembers.headerType")}</TableCell>
              <TableCell align={direction === "ltr" ? "left" : "right"} sx={{
                ...cellHeadStyle,
                width: '15%'
              }} >{t("workspaceUsersMembers.headerStatus")}</TableCell>
              <TableCell align={direction === "ltr" ? "center" : "center"} sx={{
                ...cellHeadStyle,
                width: '15%'
              }}>{t("workspaceUsersMembers.headerActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{
            background: "var(--background-default)",
            "& .MuiTableRow-root": {
              backgroundColor: 'var(--color-surface)',
              '&:hover': {
                backgroundColor: 'var(--color-surface-muted)',
              },
              '&:last-child td': {
                borderBottom: 0,
              }
            }
          }}>
            {sortedAndFilteredWorkspaceUserList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ border: 0, padding: '48px 16px' }}>
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center"
                    sx={{ textAlign: 'center' }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'var(--secondary-text-color)',
                        fontFamily: '"Inter", "Assistant", sans-serif',
                        marginBottom: 1
                      }}
                    >
                      {t("workspaceUsersMembers.emptyStateTitle")}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'var(--tertiary-text-color)',
                        fontFamily: '"Inter", "Assistant", sans-serif'
                      }}
                    >
                      {t("workspaceUsersMembers.emptyStateDescription")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              sortedAndFilteredWorkspaceUserList.map((workspaceUser: BlueticksWorkspaceUser, index) => (
                currentWorkspace ? (
                  <Row
                    user={workspaceUser}
                    workspace={currentWorkspace}
                    key={index}
                    readOnly={readOnly}
                  />
                ) : null
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return buildTable();
}

interface RowProps {
  user: BlueticksWorkspaceUser;
  workspace: Workspace;
  readOnly?: boolean;
}

const Row = ({ user, workspace,readOnly }: RowProps) => {
  const { t } = useTranslation(namespace);
  const direction = useAppSelector((state) => state.UserReducer.direction);

  const cellBodyStyle = {
    fontFamily: '"Inter", "Assistant", sans-serif',
    padding: '16px',
    fontSize: '14px',
    borderBottom: '1px solid var(--border-color, #F3F4F6)',
    color: 'var(--color-text)',
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, { isLoading: isAuthenticating }] = useAuthenticateMutation();
  const [assignInviteId, { isLoading: isAssigningInviteId }] = useAssignWorkspaceUserInviteIdMutation();
  const dispatch = useAppDispatch()
  const loading = useMemo(() => isAuthenticating, [isAuthenticating]);
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);

  const handleDeleteConfirmationClosed = useCallback((shouldDelete?: boolean) => {
    if (shouldDelete) {
      dispatch(DBWorkspaceUserThunks.delete({
        entity: user
      }));
    }
    setDeletePromptOpen(false);
  }, [dispatch, user])

  const handleResendInvite = useCallback(async () => {
    if (!workspace?._id || !user?.email) {
      console.error('Missing workspace or user data for resend invite:', { workspace, user });
      dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteResendError")));
      return null;
    }

    try {
      const response = await assignInviteId({
        workspaceId: workspace._id,
        email: user.email
      }).unwrap();
      
      if (response.success && response.workspaceUser) {
        console.log('Successfully resent invite:', response.workspaceUser);
        
        // 1. Refresh the workspace users list to show the updated data
        dispatch(DBWorkspaceUserThunks.find({ query: { workspaceId: workspace._id } }));
        
        // 2. Generate and copy the invite link to clipboard
        const inviteUrl = `${appConfig.APP_ENDPOINT}/join-workspace/${workspace._id}?inviteId=${response.workspaceUser._id}`;
        try {
          await navigator.clipboard.writeText(inviteUrl);
          console.log('Invite resent and copied invite URL:', inviteUrl);
          dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteResent")));
        } catch (clipboardError) {
          console.error('Failed to copy invite link to clipboard:', clipboardError);
          // Still show success message for invite resend, but mention copy failure
          dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteLinkCopied")));
        }
        
        return response.workspaceUser._id;
      } else {
        console.error('Failed to resend invite:', response.error);
        dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteResendError")));
        return null;
      }
    } catch (error) {
      console.error('Error calling assign-workspace-user-invite-id action:', error);
      dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteResendError")));
      return null;
    }
  }, [workspace?._id, user?.email, assignInviteId, dispatch, t]);

  const handleCopyInviteLink = useCallback(async () => {
    if (!workspace?._id || !user?._id) {
      console.error('Missing workspace or user data:', { workspace, user });
      dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteLinkCopyError")));
      return;
    }
    
    const inviteUrl = `${appConfig.APP_ENDPOINT}/join-workspace/${workspace._id}?inviteId=${user._id}`;
    console.log('Generated invite URL:', inviteUrl);
    
    try {
      await navigator.clipboard.writeText(inviteUrl);
      dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteLinkCopied")));
    } catch (err) {
      console.error('Failed to copy invite link:', err);
      dispatch(taskActions.setTaskSnackbarMessage(t("workspaceUsersMembers.inviteLinkCopyError")));
    }
  }, [workspace?._id, user?._id, dispatch, t])

  const changeUserRole = useCallback(async (role: string, user: BlueticksWorkspaceUser) => {
    const res: any = await dispatch(DBWorkspaceUserThunks.patch({
      entity: {
        _id: user._id,
        role
      }
    }));
    if (res?.payload?.message) {
      dispatch(taskActions.setTaskSnackbarMessage(res?.payload?.message));
    }
  }, [dispatch]);

  const changeUserStatus = useCallback(async (event: SelectChangeEvent<string>, user: BlueticksWorkspaceUser) => {
    const status = event.target.value;
    dispatch(DBWorkspaceUserThunks.patch({
      entity: {
        _id: user._id,
        status
      }
    }));
  }, [dispatch]);

  const buildLoader = () => (
    <TableRow>
      <TableCell colSpan={5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ p: 1 }}
        >
          <CircularProgress></CircularProgress>
        </Stack>
      </TableCell>
    </TableRow>
  );

  const buildRow = () => (

    <>
      <PromptDeleteAction title={`${t("workspaceUsersMembers.removeUserPrompt")} ${user.email}?`} handleClose={handleDeleteConfirmationClosed} open={deletePromptOpen} />
      <TableRow>
        <TableCell align={direction === "ltr" ? "left" : "right"} sx={cellBodyStyle} component="td" scope="row">
          <Stack direction="row" spacing={2} alignItems="center" sx={{ opacity: user?.status === 'inactive' ? '0.6' : '1' }}>
            <PersonAvatar 
              textSize={16} 
              size={40} 
              email={user.email}
              profileImg={user?.profile?.profileImg}
              lastName={user?.profile?.lastName}
              firstName={user?.profile?.firstName} 
            />
            <Stack spacing={0.5}>
              <span style={{
                fontSize: '14px',
                fontWeight: '500',
                lineHeight: '20px',
                color: 'var(--primary-text-color)',
                fontFamily: '"Inter", "Assistant", sans-serif',
              }}>
                {userDisplayName(user?.profile) ?? user.email}
              </span>
            </Stack>
          </Stack>
        </TableCell>
        <TableCell align={direction === "ltr" ? "left" : "right"} sx={cellBodyStyle}>
          <span style={{
            fontSize: '14px',
            color: 'var(--secondary-text-color)',
            fontFamily: '"Inter", "Assistant", sans-serif',
          }}>
            {user?.email}
          </span>
        </TableCell>

        <TableCell align={direction === "ltr" ? "left" : "right"} sx={cellBodyStyle}>
          <SelectUserRole
            disabled={readOnly}
            onChange={(role) => {
              changeUserRole(role, user)
            }}
            user={user} 
          />
        </TableCell>
        
        <TableCell align={direction === "ltr" ? "left" : "right"} sx={cellBodyStyle}>
          {user.status === 'pending' ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <div style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#F59E0B',
              }} />
              <span style={{
                fontSize: '14px',
                color: 'var(--color-text)',
                fontFamily: '"Inter", "Assistant", sans-serif',
                fontWeight: '500',
              }}>
                {t("workspaceUsersMembers.statusPending")}
              </span>
              {workspace?._id && user?._id && (
                <Tooltip title={t("workspaceUsersMembers.resendInvite")}>
                  <IconButton 
                    onClick={handleResendInvite}
                    disabled={isAssigningInviteId}
                    size="small"
                    sx={{
                      backgroundColor: isAssigningInviteId ? 'var(--disabled-bg, #F9FAFB)' : 'var(--primary-bg, #DBEAFE)',
                      color: isAssigningInviteId ? 'var(--disabled-color, #D1D5DB)' : 'var(--primary-color, #2563EB)',
                      width: 28,
                      height: 28,
                      ml: 1,
                      '&:hover': {
                        backgroundColor: isAssigningInviteId ? 'var(--disabled-bg, #F9FAFB)' : 'var(--primary-hover-bg, #BFDBFE)',
                        color: isAssigningInviteId ? 'var(--disabled-color, #D1D5DB)' : 'var(--primary-hover-color, #1D4ED8)',
                      },
                      '&:disabled': {
                        backgroundColor: 'var(--disabled-bg, #F9FAFB)',
                        color: 'var(--disabled-color, #D1D5DB)'
                      }
                    }}
                  >
                    {isAssigningInviteId ? (
                      <CircularProgress size={16} sx={{ color: 'var(--disabled-color, #D1D5DB)' }} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {workspace?._id && user?._id && (
                <Tooltip title={t("workspaceUsersMembers.copyInviteLink")}>
                  <IconButton
                    onClick={handleCopyInviteLink}
                    size="small"
                    aria-label={t("workspaceUsersMembers.copyInviteLink")}
                    data-testid="copy-invite-link"
                    sx={{
                      backgroundColor: 'var(--primary-bg, #DBEAFE)',
                      color: 'var(--primary-color, #2563EB)',
                      width: 28,
                      height: 28,
                      '&:hover': {
                        backgroundColor: 'var(--primary-hover-bg, #BFDBFE)',
                        color: 'var(--primary-hover-color, #1D4ED8)',
                      },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ) : (
            <FormControl size="small">
              <Select
                disabled={(user.status === 'pending' || user?.role === 'owner') ?? true}
                sx={{
                  minWidth: '100px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--input-bg, #F9FAFB)',
                  fontSize: '14px',
                  fontFamily: '"Inter", "Assistant", sans-serif',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid var(--border-color, #E5E7EB)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--border-hover, #D1D5DB)',
                  },
                }}
                value={user.status}
                onChange={(event) => changeUserStatus(event, user)}
              >
                {ValidManualStatusList.map((status, index) => (
                  <MenuItem 
                    key={index} 
                    value={status}
                    sx={{
                      fontSize: '14px',
                      fontFamily: '"Inter", "Assistant", sans-serif',
                      textTransform: 'capitalize',
                    }}
                  >
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </TableCell>
        <TableCell align="center" sx={cellBodyStyle}>
          {user?.role !== 'owner' && (
            <Tooltip title={t("workspaceUsersMembers.removeUserPrompt")}>
              <IconButton 
                disabled={readOnly} 
                onClick={() => setDeletePromptOpen(true)}
                size="small"
                sx={{
                  backgroundColor: readOnly ? 'var(--disabled-bg, #F9FAFB)' : 'var(--danger-bg, #FEF2F2)',
                  color: readOnly ? 'var(--disabled-color, #D1D5DB)' : 'var(--danger-color, #EF4444)',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: readOnly ? 'var(--disabled-bg, #F9FAFB)' : 'var(--danger-hover-bg, #FEE2E2)',
                    color: readOnly ? 'var(--disabled-color, #D1D5DB)' : 'var(--danger-hover-color, #DC2626)',
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--disabled-bg, #F9FAFB)',
                    color: 'var(--disabled-color, #D1D5DB)',
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    </>
  );

  return loading ? buildLoader() : buildRow();
};
